import React, { useState, useMemo } from 'react'
import { useT } from '../../lib/i18n'
import { useQuery, useTasks, type ProjectTask } from '../../lib/api'
import { usePersistedState } from '../../lib/usePersistedState'

/* ── Types locaux ─────────────────────────────────────────────── */
interface ProjectSummary { id: string; name: string }

type StatusFilter   = 'all' | 'in_progress' | 'todo' | 'blocked' | 'done'
type PriorityFilter = 'all' | 'P0' | 'P1' | 'P2' | 'P3'

/* ── Constantes de style ──────────────────────────────────────── */
const STATUS_ICON: Record<string, string> = {
  done: '✅', in_progress: '🔄', blocked: '❌', todo: '⬜',
}
const STATUS_LABEL: Record<string, string> = {
  done: 'Terminé', in_progress: 'En cours', blocked: 'Bloqué', todo: 'À faire',
}
const STATUS_COLOR: Record<string, string> = {
  done:        'var(--health-good)',
  in_progress: 'var(--accent)',
  blocked:     'var(--health-crit)',
  todo:        'var(--text-dim)',
}
const PRIORITY_COLOR: Record<string, string> = {
  P0: '#dc2626', P1: '#ea580c', P2: '#ca8a04', P3: '#16a34a',
}

/* ── Helpers ──────────────────────────────────────────────────── */
function priorityOrder(p?: string): number {
  return p === 'P0' ? 0 : p === 'P1' ? 1 : p === 'P2' ? 2 : p === 'P3' ? 3 : 4
}

function groupBySprint(tasks: ProjectTask[]): Map<string, ProjectTask[]> {
  const map = new Map<string, ProjectTask[]>()
  for (const t of tasks) {
    const key = t.sprint ?? 'Sans sprint'
    const arr = map.get(key) ?? []
    arr.push(t)
    map.set(key, arr)
  }
  return map
}

/* ── Barre de progression ─────────────────────────────────────── */
function MiniBar({ done, total }: { done: number; total: number }) {
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0
  const color = pct === 100 ? 'var(--health-good)' : pct >= 60 ? 'var(--accent)' : 'var(--health-warn)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 400ms' }} />
      </div>
      <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color, fontWeight: 700, flexShrink: 0 }}>{pct}%</span>
    </div>
  )
}

/* ── Carte tâche ──────────────────────────────────────────────── */
function TaskCard({ task, index }: { task: ProjectTask; index: number }) {
  const isDone  = task.status === 'done'
  const sColor  = STATUS_COLOR[task.status]  ?? 'var(--text-dim)'
  const pColor  = task.priority ? PRIORITY_COLOR[task.priority] : undefined

  return (
    <div
      className="animate-in"
      style={{
        display: 'grid',
        gridTemplateColumns: '20px 1fr auto',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '9px 14px',
        borderBottom: '1px solid var(--border)',
        opacity: isDone ? 0.5 : 1,
        animationDelay: `${index * 0.02}s`,
        transition: 'background 100ms',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)'}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ''}
    >
      {/* Icône */}
      <span style={{ fontSize: 13, marginTop: 2, flexShrink: 0 }}>
        {STATUS_ICON[task.status] ?? '⬜'}
      </span>

      {/* Titre + meta */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--text-base)',
          color: isDone ? 'var(--text-muted)' : 'var(--text)',
          textDecoration: isDone ? 'line-through' : 'none',
          fontWeight: isDone ? 400 : 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
        }}>
          {task.title}
        </div>
        <div style={{ display: 'flex', gap: '5px', marginTop: '3px', flexWrap: 'wrap', alignItems: 'center' }}>
          {task.priority && (
            <span style={{
              fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
              padding: '1px 6px', borderRadius: 4,
              background: `color-mix(in srgb, ${pColor ?? 'var(--accent)'} 15%, transparent)`,
              color: pColor ?? 'var(--accent)',
              border: `1px solid color-mix(in srgb, ${pColor ?? 'var(--accent)'} 30%, transparent)`,
            }}>
              {task.priority}
            </span>
          )}
          {task.agent && (
            <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {task.agent}
            </span>
          )}
          <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {task.id}
          </span>
        </div>
      </div>

      {/* Badge statut */}
      <span style={{
        fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
        color: sColor, flexShrink: 0,
        background: `color-mix(in srgb, ${sColor} 12%, transparent)`,
        padding: '2px 7px', borderRadius: 4,
        marginTop: 2, whiteSpace: 'nowrap',
      }}>
        {STATUS_LABEL[task.status] ?? task.status}
      </span>
    </div>
  )
}

/* ── Section sprint (collapsible) ─────────────────────────────── */
function SprintGroup({ name, tasks, index }: { name: string; tasks: ProjectTask[]; index: number }) {
  const [open, setOpen] = useState(true)

  const done       = tasks.filter(t => t.status === 'done').length
  const inProgress = tasks.filter(t => t.status === 'in_progress').length
  const blocked    = tasks.filter(t => t.status === 'blocked').length

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* En-tête sprint */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          background: 'var(--surface)',
          cursor: 'pointer',
          userSelect: 'none',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)'}
        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface)'}
      >
        {/* Toggle */}
        <span style={{
          fontSize: 10, color: 'var(--text-dim)', flexShrink: 0,
          transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 150ms',
          display: 'inline-block',
        }}>▼</span>

        {/* Nom sprint */}
        <span style={{
          fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text)',
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </span>

        {/* Barre progression */}
        <div style={{ width: '100px', flexShrink: 0 }}>
          <MiniBar done={done} total={tasks.length} />
        </div>

        {/* Compteurs */}
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          {done > 0        && <span style={{ color: 'var(--health-good)', fontWeight: 700 }}>✅{done}</span>}
          {inProgress > 0  && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>🔄{inProgress}</span>}
          {blocked > 0     && <span style={{ color: 'var(--health-crit)', fontWeight: 700 }}>❌{blocked}</span>}
          <span style={{ color: 'var(--text-dim)' }}>{tasks.length}</span>
        </div>
      </div>

      {/* Liste tâches */}
      {open && tasks.map((task, i) => <TaskCard key={task.id} task={task} index={i} />)}
    </div>
  )
}

/* ── Pill de filtre ───────────────────────────────────────────── */
function Pill({ active, onClick, children, count }: {
  active: boolean; onClick: () => void; children: React.ReactNode; count?: number
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '4px 10px',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: '999px',
        background: active ? 'var(--accent-dim)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        fontSize: 'var(--text-xs)', fontFamily: 'var(--font-sans)',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer', transition: 'all 120ms', whiteSpace: 'nowrap',
      }}
    >
      {children}
      {count !== undefined && (
        <span style={{
          background: active ? 'var(--accent)' : 'var(--surface-2)',
          color: active ? 'white' : 'var(--text-dim)',
          borderRadius: '999px', padding: '0 5px',
          fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
          minWidth: 16, textAlign: 'center',
        }}>
          {count}
        </span>
      )}
    </button>
  )
}

/* ── Vue principale Tasks ─────────────────────────────────────── */
export default function Tasks() {
  const t = useT()
  const [selectedProject, setSelectedProject] = usePersistedState<string | null>('cc-tasks-project', null)
  const [statusFilter,    setStatusFilter]    = usePersistedState<StatusFilter>('cc-tasks-status', 'all')
  const [priorityFilter,  setPriorityFilter]  = usePersistedState<PriorityFilter>('cc-tasks-priority', 'all')
  const [search,          setSearch]          = usePersistedState<string>('cc-tasks-search', '')

  const { data: projectsData, loading: projectsLoading } =
    useQuery<{ projects: ProjectSummary[] }>('/projects', 60_000)

  const projects = projectsData?.projects ?? []

  /* Sélection automatique du premier projet */
  const effectiveProject = selectedProject ?? projects[0]?.id ?? null

  const { data: tasksData, loading: tasksLoading } = useTasks(effectiveProject)
  const allTasks = tasksData?.tasks ?? []

  /* ── Filtrage ──────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let list = allTasks
    if (statusFilter   !== 'all') list = list.filter(t => t.status === statusFilter)
    if (priorityFilter !== 'all') list = list.filter(t => t.priority === priorityFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(t => t.title.toLowerCase().includes(q) || t.id.toLowerCase().includes(q))
    }
    return list
  }, [allTasks, statusFilter, priorityFilter, search])

  /* ── Groupes sprint + tri ──────────────────────────────────── */
  const sprintGroups = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => priorityOrder(a.priority) - priorityOrder(b.priority))
    return groupBySprint(sorted)
  }, [filtered])

  /* ── KPIs pour les filtres ─────────────────────────────────── */
  const counts = useMemo(() => ({
    all:         allTasks.length,
    in_progress: allTasks.filter(t => t.status === 'in_progress').length,
    todo:        allTasks.filter(t => t.status === 'todo').length,
    blocked:     allTasks.filter(t => t.status === 'blocked').length,
    done:        allTasks.filter(t => t.status === 'done').length,
    P0:          allTasks.filter(t => t.priority === 'P0').length,
    P1:          allTasks.filter(t => t.priority === 'P1').length,
    P2:          allTasks.filter(t => t.priority === 'P2').length,
    P3:          allTasks.filter(t => t.priority === 'P3').length,
  }), [allTasks])

  const isLoading = projectsLoading || tasksLoading

  /* ── Rendu ─────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Sidebar projets ─────────────────────────────────── */}
      <div style={{
        width: '200px', flexShrink: 0,
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '10px 12px 8px',
          borderBottom: '1px solid var(--border)',
          fontSize: 'var(--text-xs)', fontWeight: 700,
          color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          Projets
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '6px 8px' }}>
          {projects.map(p => {
            const isActive = effectiveProject === p.id
            return (
              <button
                key={p.id}
                onClick={() => { setSelectedProject(p.id); setStatusFilter('all'); setPriorityFilter('all'); setSearch('') }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '7px 10px', marginBottom: '3px',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                  fontSize: 'var(--text-sm)', cursor: 'pointer',
                  transition: 'all 120ms',
                  fontWeight: isActive ? 600 : 400,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)' }}
                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                {p.name}
              </button>
            )
          })}
        </div>
        {tasksData?.source && (
          <div style={{
            padding: '6px 12px', borderTop: '1px solid var(--border)',
            fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
          }}>
            source : {tasksData.source}
          </div>
        )}
      </div>

      {/* ── Zone principale ─────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* ── Barre de filtres ──────────────────────────────── */}
        <div style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-2)',
          display: 'flex', flexDirection: 'column', gap: '8px',
          flexShrink: 0,
        }}>
          {/* Ligne 1 : recherche + projet header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--text)' }}>
              {effectiveProject ?? '—'}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {filtered.length} / {allTasks.length} tâches
            </span>
            <div style={{ flex: 1 }} />
            <input
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '4px 10px', borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                background: 'var(--surface)', color: 'var(--text)',
                fontSize: 'var(--text-sm)', outline: 'none',
                width: '180px',
              }}
            />
          </div>

          {/* Ligne 2 : filtres statut */}
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600, marginRight: '2px' }}>Statut</span>
            <Pill active={statusFilter === 'all'}         onClick={() => setStatusFilter('all')}         count={counts.all}>📋 Toutes</Pill>
            <Pill active={statusFilter === 'in_progress'} onClick={() => setStatusFilter('in_progress')} count={counts.in_progress}>🔄 En cours</Pill>
            <Pill active={statusFilter === 'todo'}        onClick={() => setStatusFilter('todo')}        count={counts.todo}>⬜ À faire</Pill>
            {counts.blocked > 0 && (
              <Pill active={statusFilter === 'blocked'} onClick={() => setStatusFilter('blocked')} count={counts.blocked}>❌ Bloqué</Pill>
            )}
            <Pill active={statusFilter === 'done'} onClick={() => setStatusFilter('done')} count={counts.done}>✅ Terminé</Pill>
          </div>

          {/* Ligne 3 : filtres priorité */}
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600, marginRight: '2px' }}>Priorité</span>
            <Pill active={priorityFilter === 'all'} onClick={() => setPriorityFilter('all')}>Toutes</Pill>
            {(['P0', 'P1', 'P2', 'P3'] as const).map(p => counts[p] > 0 && (
              <Pill key={p} active={priorityFilter === p} onClick={() => setPriorityFilter(p)} count={counts[p]}>
                <span style={{ color: PRIORITY_COLOR[p], fontWeight: 700 }}>{p}</span>
              </Pill>
            ))}
          </div>
        </div>

        {/* ── Liste tâches groupées par sprint ──────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {isLoading && allTasks.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
              {t('loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📋</div>
              <div style={{ fontSize: 'var(--text-base)' }}>
                {allTasks.length === 0 ? 'Aucun TASKS.md trouvé pour ce projet.' : 'Aucune tâche ne correspond aux filtres.'}
              </div>
              {allTasks.length > 0 && (
                <button
                  onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); setSearch('') }}
                  style={{
                    marginTop: '12px', padding: '6px 14px',
                    background: 'var(--accent)', color: 'white',
                    border: 'none', borderRadius: 'var(--radius)',
                    cursor: 'pointer', fontSize: 'var(--text-sm)',
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            [...sprintGroups.entries()].map(([sprint, tasks], i) => (
              <SprintGroup key={sprint} name={sprint} tasks={tasks} index={i} />
            ))
          )}
        </div>
      </div>
    </div>
  )
}
